import { TFunction } from "i18next";
import { ExtendedLicense } from "../../redux/actions/licenses/licenceTypes";

export const normalizeText = (text: string) => {
  return text
    .toLowerCase()
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
};

export const inarixExistingOperators = [
  { value: "$ilike", label: "contains" }, // contains
  { value: "$eq", label: "equals" }, // equals
  { value: "$gt", label: ">" }, // greater than
  { value: "$lt", label: "<" }, // less than
  { value: "$ne", label: "!=" }, // not equal
  { value: "$in", label: "is any of" },
];

export const getOperatorsFromAvailableFilters = (
  availableFilters: string[],
) => {
  if (!availableFilters || availableFilters.length === 0) {
    return [];
  }

  const allOperators = inarixExistingOperators;

  const matchedOperators = allOperators.filter((operator) =>
    availableFilters.includes(operator.value),
  );

  const operatorsWithCallback = matchedOperators.map((operator) => ({
    ...operator,
  }));

  return operatorsWithCallback;
};

export const returnTheLabelOperator = (operatorValue: string) => {
  const operator = inarixExistingOperators.find(
    (op) => op.value === operatorValue,
  );
  return operator?.label;
};

// Helper function to handle LOCAL filters based on service type
export const applyLocalFilter = (filterItem: any, user: any): boolean => {
  // Handle numeric operator filters (service: "operator")
  if (filterItem.service === "operator") {
    let numericValue: number;

    // Special handling for balance field
    if (filterItem.field === "balance") {
      const licenses = user.licenses || [];
      numericValue = licenses.reduce(
        (sum: number, license: ExtendedLicense) => {
          return sum + (license.availableCredits || 0);
        },
        0,
      );
    } else {
      // Get field value and convert to number
      const fieldValue = (user as any)[filterItem.field];
      numericValue = Number(fieldValue);
    }

    // Get filter value and convert to number
    const filterValue = Array.isArray(filterItem.value)
      ? filterItem.value[0]
      : filterItem.value;
    const numericFilterValue = Number(filterValue);

    // Skip filter if conversion failed
    if (Number.isNaN(numericFilterValue) || Number.isNaN(numericValue))
      return true;

    // Apply the operator
    switch (filterItem.operator) {
      case "$eq":
        return numericValue === numericFilterValue;
      case "$gt":
        return numericValue > numericFilterValue;
      case "$lt":
        return numericValue < numericFilterValue;
      case "$ne":
        return numericValue !== numericFilterValue;
      default:
        return true;
    }
  }

  // Handle text filters (service: "text")
  if (filterItem.service === "text") {
    // Get field value as string
    const fieldValue = String((user as any)[filterItem.field] || "");

    // Get filter value
    const filterValue = Array.isArray(filterItem.value)
      ? filterItem.value[0]
      : filterItem.value;
    const filterText = String(filterValue || "");

    // Normalize both values for comparison (remove accents and lowercase)
    const normalizedFieldValue = fieldValue
      .normalize("NFD")
      .replaceAll(/\p{Diacritic}/gu, "")
      .toLowerCase();

    const normalizedFilterText = filterText
      .normalize("NFD")
      .replaceAll(/\p{Diacritic}/gu, "")
      .toLowerCase();

    // Apply the operator
    switch (filterItem.operator) {
      case "$ilike":
        return normalizedFieldValue.includes(normalizedFilterText);
      case "$eq":
        return normalizedFieldValue === normalizedFilterText;
      case "$ne":
        return normalizedFieldValue !== normalizedFilterText;
      default:
        return true;
    }
  }

  return true;
};

export const FirstLetterUpperCase = (text: string) => {
  if (!text) return text;
  return text.charAt(0).toUpperCase() + text.slice(1);
};

export const getTranslatedOperatorLabel = (
  operatorValue: string,
  t: TFunction,
) => {
  switch (operatorValue) {
    case "$ilike":
      return t("custom_filter.operators.contains");
    case "$eq":
      return t("custom_filter.operators.equals");
    case "$gt":
      return t("custom_filter.operators.greater_than");
    case "$lt":
      return t("custom_filter.operators.less_than");
    case "$ne":
      return t("custom_filter.operators.not_equal");
    case "$in":
      return t("custom_filter.operators.is_any_of");
    default:
      return operatorValue;
  }
};
